import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';

import { download } from 'utils/download';

import { mutations } from 'RestAPIs/rapini';
import { showSnackbar } from 'components/shared';

import type { AppointmentExportFormat } from 'generated/index';

import { buildAppointmentsFilter } from './useFilteredAppointments';
import type { UseFilteredAppointmentsParams } from './useFilteredAppointments';

/** The export's row-cap refusal, as `ExportAppointmentsFilteredQueryHandler.TOO_MANY_ROWS` sends it. */
const TOO_MANY_ROWS = 'appointments_overview.export.too_many_rows';

/** `filterSummary`'s `maxLength` in `ExportAppointmentsFilteredRequest.yaml`: a longer one is a 400. */
export const MAX_FILTER_SUMMARY_LENGTH = 500;

const SEPARATOR = ', ';

/**
 * The filter names as the file header prints them, within the request's limit. Past it the names that
 * fit are followed by how many more there are ("Jan Jansen, Noord en 12 meer"), so a filter on many
 * people or plan units still downloads.
 */
export const toFilterSummary = (
  names: string[],
  moreLabel: (count: number) => string
): string | undefined => {
  if (names.length === 0) return undefined;

  const summary = names.join(SEPARATOR);
  if (summary.length <= MAX_FILTER_SUMMARY_LENGTH) return summary;

  for (let shown = names.length - 1; shown > 0; shown--) {
    const shortened = `${names.slice(0, shown).join(SEPARATOR)} ${moreLabel(
      names.length - shown
    )}`;
    if (shortened.length <= MAX_FILTER_SUMMARY_LENGTH) return shortened;
  }
  // Not even the first name fits beside the count.
  return `${summary.slice(0, MAX_FILTER_SUMMARY_LENGTH - 1)}…`;
};

export type UseDownloadAppointmentsParams = UseFilteredAppointmentsParams & {
  /** What the overview is filtered on, one name each; the file header prints them. */
  filterNames: string[];
};

/**
 * Downloads the appointments the overview shows as an Excel or PDF file. It sends the overview's own
 * filter (`buildAppointmentsFilter`), so the file holds the rows on screen. The backend renders the
 * file and answers with a short-lived URL; the file itself is then fetched from there, so
 * `isDownloading` covers both steps.
 */
export const useDownloadAppointments = ({
  filterNames,
  ...filter
}: UseDownloadAppointmentsParams) => {
  const { t } = useTranslation('common');
  const [isDownloading, setIsDownloading] = useState(false);
  const { mutateAsync } = mutations.useExportAppointmentFiltered();

  const downloadAppointments = async (format: AppointmentExportFormat) => {
    if (!filter.startDate || !filter.endDate) return;

    setIsDownloading(true);
    try {
      const { downloadUrl, filename } = await mutateAsync({
        filter: buildAppointmentsFilter(filter),
        format,
        filterSummary: toFilterSummary(filterNames, (count) =>
          t('appointmentsOverview.download.moreFilters', { count })
        )
      });
      await download(downloadUrl, filename);
    } catch (error) {
      // Only the row cap asks for a narrower filter; any other refusal (a 400 included) is a failure.
      const isTooMany =
        isAxiosError(error) &&
        error.response?.data?.error?.message === TOO_MANY_ROWS;
      showSnackbar({
        severity: 'error',
        message: t(
          isTooMany
            ? 'appointmentsOverview.download.tooMany'
            : 'appointmentsOverview.download.failure'
        )
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { downloadAppointments, isDownloading };
};
